import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Select, MenuItem, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ExcelJS from 'exceljs';
import CustomDatePicker from '../form-components/CustomDatePicker';
import { dropdownMenuProps, selectFieldSx } from '../../utils/dropdownStyles';
import LocalText from '../common/LocalText';
import { useToast } from '../../context/ToastContext';
import {
  getDepositDetail, getDepositDetailGlobal, downloadWireDocument, downloadWireDocumentFor, uploadWireDocument,
} from '../../api/paymentApi';
import { formatAmount, formatDate, formatDateTime } from '../../utils/currencyFormat';
import WireDocumentUploadDialog from './WireDocumentUploadDialog';

const TX_PAGE_SIZE = 25;

// Excel export styling — mirrors TransactionTable.jsx's downloadExcel constants exactly.
const EXCEL_BRAND  = 'FF0A326D';
const EXCEL_BORDER = 'FFE2E8F0';
const EXCEL_ZEBRA   = 'FFF8FAFC';
const EXCEL_TEXT    = 'FF1E293B';
const EXCEL_SUBTLE  = 'FF64748B';

const TXN_TYPE_CONFIG = {
  Purchase: { bg: '#eff6ff', color: '#1d4ed8' },
  Refund:   { bg: '#fdf2f8', color: '#be185d' },
};

const TxnTypeBadge = ({ type }) => {
  const cfg = TXN_TYPE_CONFIG[type] ?? { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box sx={{ display: 'inline-block', px: 1.25, py: 0.4, borderRadius: '8px', bgcolor: cfg.bg, fontSize: 11, fontWeight: 700, color: cfg.color }}>
      <LocalText>{type}</LocalText>
    </Box>
  );
};

// Same badge configs/markup as TransactionTable.jsx (each file keeps its own small copy).
const STATUS_CONFIG = {
  Succeeded:            { bg: '#dcfce7', color: '#15803d' },
  'Partially Refunded': { bg: '#ede9fe', color: '#7c3aed' },
  'Fully Refunded':     { bg: '#f3e8ff', color: '#6d28d9' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '999px', bgcolor: cfg.bg, fontSize: 11, fontWeight: 700, color: cfg.color }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
      <LocalText>{status}</LocalText>
    </Box>
  );
};

const GATEWAY_CONFIG = {
  Stripe: { bg: '#eef2ff', color: '#4f46e5' },
  PayPal: { bg: '#dbeafe', color: '#1d4ed8' },
};

const GatewayBadge = ({ gateway }) => {
  const cfg = GATEWAY_CONFIG[gateway] ?? { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box sx={{ display: 'inline-block', px: 1.25, py: 0.4, borderRadius: '8px', bgcolor: cfg.bg, fontSize: 11, fontWeight: 700, color: cfg.color }}>
      {gateway}
    </Box>
  );
};

const isRefundedStatus = (s) => s === 'Partially Refunded' || s === 'Fully Refunded';

// Detail drill-down for a single deposit: summary header, wire-transfer document card
// (view/download for everyone, upload/replace for Finance only), and a paginated,
// filterable list of the deposit's transactions with its own Export Report action.
const DepositDetail = ({ deposit, showGlobalFilters = false, canManageDeposits = false, onBack }) => {
  const { toast } = useToast();

  const [summary, setSummary] = useState(deposit);
  const [notFound, setNotFound] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [txTotalCount, setTxTotalCount] = useState(0);
  const [loadingTx, setLoadingTx] = useState(true);
  const [txPage, setTxPage] = useState(0);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [committedDateRange, setCommittedDateRange] = useState({ from: '', to: '' });
  const [transactionType, setTransactionType] = useState('');

  const [exporting, setExporting] = useState(false);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isDateRangeError = Boolean(dateFrom && dateTo && dateFrom > dateTo);
  const hasDocument = !!summary?.wireTransferDocument;

  const buildFilterParams = useCallback(() => ({
    dateFrom: committedDateRange.from || undefined,
    dateTo: committedDateRange.to || undefined,
    transactionType: transactionType || undefined,
  }), [committedDateRange, transactionType]);

  const fetchDetailFn = useCallback((params) => (
    showGlobalFilters
      ? getDepositDetailGlobal(deposit.spaceId, deposit.depositId, params)
      : getDepositDetail(deposit.depositId, params)
  ), [showGlobalFilters, deposit.spaceId, deposit.depositId]);

  const fetchDetail = useCallback(() => {
    setLoadingTx(true);
    fetchDetailFn({ page: txPage, pageSize: TX_PAGE_SIZE, ...buildFilterParams() })
      .then((data) => {
        setSummary(data?.summary ?? deposit);
        setTransactions(data?.transactions?.items ?? []);
        setTxTotalCount(data?.transactions?.totalCount ?? 0);
        setNotFound(false);
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setNotFound(true);
        }
        // non-404 errors are shown globally via axios interceptor → toast
        setTransactions([]);
        setTxTotalCount(0);
      })
      .finally(() => setLoadingTx(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDetailFn, txPage, buildFilterParams]);

  useEffect(() => { setTxPage(0); }, [committedDateRange, transactionType]);
  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const txTotalPages = Math.max(1, Math.ceil(txTotalCount / TX_PAGE_SIZE));

  const handleDateFromChange = (val) => {
    setDateFrom(val);
    if (!val && !dateTo) setCommittedDateRange({ from: '', to: '' });
  };
  const handleDateToChange = (val) => {
    setDateTo(val);
    if (dateFrom && val && dateFrom <= val) setCommittedDateRange({ from: dateFrom, to: val });
    else if (!val) setCommittedDateRange({ from: '', to: '' });
  };
  const clearFilters = () => {
    setDateFrom(''); setDateTo(''); setCommittedDateRange({ from: '', to: '' }); setTransactionType('');
  };

  // ── Document view/download ───────────────────────────────────────────────
  const fetchDocumentBlob = () => (
    showGlobalFilters
      ? downloadWireDocumentFor(deposit.spaceId, deposit.depositId)
      : downloadWireDocument(deposit.depositId)
  );

  const handleView = async () => {
    try {
      const blob = await fetchDocumentBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      // error shown globally via axios interceptor → toast
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await fetchDocumentBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = summary?.wireTransferDocument?.originalFileName || `${deposit.depositId}-wire-transfer`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      // error shown globally via axios interceptor → toast
    }
  };

  const handleUploadConfirm = async (file) => {
    setUploading(true);
    try {
      const result = await uploadWireDocument(deposit.depositId, file);
      toast.success(result?.wasReplaced ? 'Document replaced successfully.' : 'Document uploaded successfully.');
      setUploadDialogOpen(false);
      setSummary((prev) => ({ ...(prev ?? deposit), wireTransferDocument: result?.document ?? prev?.wireTransferDocument }));
    } catch {
      // error shown globally via axios interceptor → toast
    } finally {
      setUploading(false);
    }
  };

  // ── Export Report — fetches every transaction in the deposit, not just the
  // visible page, mirroring TransactionTable.jsx's own handleExportExcel pattern. ──
  const downloadDepositExcel = async (rows) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'GMS Content Management';
    workbook.created = new Date();
    const sheet = workbook.addWorksheet('Deposit Transactions');

    const columns = [
      ...(showGlobalFilters ? [
        { key: 'entity', width: 20 }, { key: 'entityId', width: 12 },
        { key: 'space', width: 20 },  { key: 'spaceId', width: 12 },
      ] : []),
      { key: 'paymentIntentId', width: 24 }, { key: 'chargeId', width: 20 },
      { key: 'transactionType', width: 16 }, { key: 'status', width: 18 },
      { key: 'eventDate', width: 20 }, { key: 'gateway', width: 12 }, { key: 'paymentMethod', width: 16 },
      { key: 'customer', width: 26 }, { key: 'customerName', width: 22 },
      { key: 'currency', width: 10 }, { key: 'chargedAmount', width: 14 }, { key: 'amount', width: 16 },
      { key: 'gatewayFee', width: 13 }, { key: 'exchangeRate', width: 12 }, { key: 'contractPct', width: 12 },
      { key: 'netAmount', width: 14 }, { key: 'merchantNetAmount', width: 16 }, { key: 'merchantNetCurrency', width: 12 },
      { key: 'refundReason', width: 26 },
    ];
    sheet.columns = columns;
    const colCount = columns.length;
    const colIndex = {};
    columns.forEach((c, i) => { colIndex[c.key] = i + 1; });

    sheet.mergeCells(1, 1, 1, colCount);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = `Deposit Report — ${summary.depositId}`;
    titleCell.font = { bold: true, size: 15, color: { argb: EXCEL_BRAND } };
    titleCell.alignment = { vertical: 'middle' };
    sheet.getRow(1).height = 26;

    sheet.mergeCells(2, 1, 2, colCount);
    const subtitleCell = sheet.getCell(2, 1);
    subtitleCell.value = `Generated ${new Date().toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}  ·  ${rows.length} transaction${rows.length !== 1 ? 's' : ''}  ·  Total ${formatAmount(summary.totalAmount, summary.currency)}`;
    subtitleCell.font = { italic: true, size: 10, color: { argb: EXCEL_SUBTLE } };
    sheet.getRow(2).height = 18;

    const HEADER_ROW = 4;
    const HEADER_LABELS = [
      ...(showGlobalFilters ? ['Entity', 'Entity ID', 'Space', 'Space ID'] : []),
      'Payment Intent ID', 'Charge ID', 'Transaction Type', 'Status', 'Date', 'Gateway', 'Payment Method',
      'Customer Email / ID', 'Customer Name', 'Currency', 'Charged Amt', 'Paid / Refund Amt', 'Gateway Fee',
      'FX Rate', 'Contract %', 'Net Amount', 'Settled Amount', 'Settled Amount Currency', 'Refund Reason',
    ];
    const headerRow = sheet.getRow(HEADER_ROW);
    HEADER_LABELS.forEach((label, i) => { headerRow.getCell(i + 1).value = label; });
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 10.5, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_BRAND } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: EXCEL_BRAND } }, bottom: { style: 'thin', color: { argb: EXCEL_BRAND } },
        left: { style: 'thin', color: { argb: EXCEL_BRAND } }, right: { style: 'thin', color: { argb: EXCEL_BRAND } },
      };
    });
    headerRow.height = 30;

    const RIGHT_ALIGN_KEYS = ['chargedAmount', 'amount', 'gatewayFee', 'exchangeRate', 'contractPct', 'netAmount', 'merchantNetAmount'];

    rows.forEach((tx, i) => {
      const row = sheet.getRow(HEADER_ROW + 1 + i);
      const values = {
        ...(showGlobalFilters ? {
          entity: summary?.entityName ?? '', entityId: summary?.entityId ?? '',
          space: summary?.spaceName ?? '',   spaceId: summary?.spaceId ?? '',
        } : {}),
        paymentIntentId: tx.paymentIntentId,
        chargeId: tx.chargeId,
        transactionType: tx.transactionType ?? '',
        status: tx.status ?? '',
        eventDate: new Date(tx.eventDate),
        gateway: tx.gateway ?? '',
        paymentMethod: tx.paymentMethod ?? '',
        customer: tx.customer ?? '',
        customerName: tx.customerName ?? '',
        currency: tx.currency ?? '',
        chargedAmount: tx.chargedAmount != null ? Number(tx.chargedAmount) : null,
        amount: (isRefundedStatus(tx.status) ? -1 : 1) * Number(tx.amount ?? 0),
        gatewayFee: tx.gatewayFee != null ? Number(tx.gatewayFee) : null,
        exchangeRate: tx.exchangeRate != null ? Number(tx.exchangeRate) : null,
        contractPct: tx.contractPercentageApplied != null ? Number(tx.contractPercentageApplied) : null,
        netAmount: tx.netAmount != null ? Number(tx.netAmount) : null,
        merchantNetAmount: tx.merchantNetAmount != null ? Number(tx.merchantNetAmount) : null,
        merchantNetCurrency: tx.merchantNetCurrency ?? '',
        refundReason: tx.refundReason ?? '',
      };
      const zebra = i % 2 === 1;
      columns.forEach((col) => {
        const cell = row.getCell(colIndex[col.key]);
        cell.value = values[col.key];
        cell.font = { size: 10, color: { argb: EXCEL_TEXT } };
        cell.alignment = { vertical: 'middle', horizontal: RIGHT_ALIGN_KEYS.includes(col.key) ? 'right' : 'left' };
        cell.border = {
          top: { style: 'thin', color: { argb: EXCEL_BORDER } }, bottom: { style: 'thin', color: { argb: EXCEL_BORDER } },
          left: { style: 'thin', color: { argb: EXCEL_BORDER } }, right: { style: 'thin', color: { argb: EXCEL_BORDER } },
        };
        if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_ZEBRA } };
      });
      row.getCell(colIndex.eventDate).numFmt = 'mmm dd, yyyy hh:mm AM/PM';
      row.getCell(colIndex.amount).numFmt = '#,##0.00;[Red]-#,##0.00';
      row.height = 18;
    });

    sheet.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: colCount } };
    sheet.views = [{ state: 'frozen', ySplit: HEADER_ROW }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${summary.depositId}_transactions.xlsx`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handleExportExcel = async () => {
    if (txTotalCount === 0 || exporting) return;
    setExporting(true);
    try {
      const data = await fetchDetailFn({ page: 0, pageSize: txTotalCount, ...buildFilterParams() });
      const rows = data?.transactions?.items ?? [];
      await downloadDepositExcel(rows);
    } catch {
      // error shown globally via axios interceptor → toast
    } finally {
      setExporting(false);
    }
  };

  const colHead = { color: '#64748b', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', py: 1.75, bgcolor: '#f8fafc' };
  const colCell = { fontSize: 12, color: '#1e293b', py: 1.5, borderBottom: '1px solid #f1f5f9' };

  const entitySpaceLabel = useMemo(() => {
    const parts = [summary?.entityName, summary?.spaceName].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  }, [summary]);

  if (notFound) {
    return (
      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', p: 6, textAlign: 'center' }}>
        <Typography fontSize={14} sx={{ color: '#64748b', mb: 2 }}>
          <LocalText>This deposit could not be found.</LocalText>
        </Typography>
        <Box component="button" onClick={onBack} sx={{ px: 2, height: 36, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0a326d' }}>
          <LocalText>Back to Deposits</LocalText>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* ── Breadcrumb ──────────────────────────────────── */}
      <Box onClick={onBack} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, color: '#64748b', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
        <ArrowBackIcon sx={{ fontSize: 14 }} />
        <Box component="span" fontWeight={700} sx={{ color: '#0a326d' }}><LocalText>Deposits</LocalText></Box>
        <Box component="span">/ {deposit.depositId}</Box>
      </Box>

      {/* ── Summary header ──────────────────────────────── */}
      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'inline-block', px: 1.5, py: 0.6, borderRadius: '8px', bgcolor: '#f1f5f9', fontSize: 14, fontWeight: 800, color: '#0a326d', fontFamily: 'monospace' }}>
              {summary?.depositId ?? deposit.depositId}
            </Box>
            <Box sx={{ display: 'flex', gap: 3.5, mt: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography fontSize={11} sx={{ color: '#94a3b8' }}><LocalText>Total Amount</LocalText></Typography>
                <Typography fontSize={16} fontWeight={800} sx={{ color: '#1e293b' }}>{formatAmount(summary?.totalAmount, summary?.currency)}</Typography>
              </Box>
              <Box>
                <Typography fontSize={11} sx={{ color: '#94a3b8' }}><LocalText>Total Transactions</LocalText></Typography>
                <Typography fontSize={16} fontWeight={800} sx={{ color: '#1e293b' }}>{summary?.chargeCount ?? 0}</Typography>
              </Box>
              <Box>
                <Typography fontSize={11} sx={{ color: '#94a3b8' }}><LocalText>Entity / Space</LocalText></Typography>
                <Typography fontSize={14} fontWeight={700} sx={{ color: '#1e293b' }}>{entitySpaceLabel}</Typography>
              </Box>
              <Box>
                <Typography fontSize={11} sx={{ color: '#94a3b8' }}><LocalText>Last Activity</LocalText></Typography>
                <Typography fontSize={14} fontWeight={700} sx={{ color: '#1e293b' }}>{formatDate(summary?.lastChargeDate)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Document card ───────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: hasDocument ? '#f8fafc' : '#fffbeb', border: `1px solid ${hasDocument ? '#e2e8f0' : '#fde68a'}`, borderRadius: 2.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: hasDocument ? '#eff6ff' : '#fef3c7', color: hasDocument ? '#1d4ed8' : '#d97706' }}>
          {hasDocument ? <DescriptionOutlinedIcon /> : <WarningAmberRoundedIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {hasDocument ? (
            <>
              <Typography fontWeight={700} fontSize={13} sx={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {summary.wireTransferDocument.originalFileName}
              </Typography>
              <Typography fontSize={11} sx={{ color: '#64748b', mt: 0.25 }}>
                <LocalText>Uploaded by</LocalText> {summary.wireTransferDocument.uploadedBy} <LocalText>on</LocalText> {formatDateTime(summary.wireTransferDocument.uploadedAt)}
              </Typography>
            </>
          ) : (
            <>
              <Typography fontWeight={700} fontSize={13} sx={{ color: '#92400e' }}>
                <LocalText>No transfer document on file</LocalText>
              </Typography>
              <Typography fontSize={11} sx={{ color: '#92400e', mt: 0.25, opacity: 0.85 }}>
                <LocalText>Legacy deposit, assigned before this feature existed</LocalText>
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {hasDocument && (
            <>
              <Box component="button" onClick={handleView} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, height: 32, border: '1px solid #dbeafe', borderRadius: '8px', bgcolor: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#0a326d' }}>
                <VisibilityOutlinedIcon sx={{ fontSize: 15 }} /><LocalText>View</LocalText>
              </Box>
              <Box component="button" onClick={handleDownload} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, height: 32, border: 'none', borderRadius: '8px', bgcolor: '#0a326d', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'white' }}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} /><LocalText>Download</LocalText>
              </Box>
              {canManageDeposits && (
                <Box component="button" onClick={() => setUploadDialogOpen(true)} sx={{ px: 1.5, height: 32, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                  <LocalText>Replace</LocalText>
                </Box>
              )}
            </>
          )}
          {!hasDocument && canManageDeposits && (
            <Box component="button" onClick={() => setUploadDialogOpen(true)} sx={{ px: 1.75, height: 32, border: 'none', borderRadius: '8px', bgcolor: '#0a326d', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'white' }}>
              <LocalText>Upload</LocalText>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Transactions ────────────────────────────────── */}
      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, borderBottom: '1px solid #f1f5f9' }}>
          <Box>
            <Typography fontWeight={700} fontSize={16} sx={{ color: '#1e293b' }}>
              <LocalText>Transactions in this deposit</LocalText>
            </Typography>
            <Typography fontSize={12} sx={{ color: '#94a3b8', mt: 0.25 }}>
              <LocalText>Paginated on screen — a deposit can include many charges.</LocalText>
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={handleExportExcel}
            disabled={txTotalCount === 0 || exporting}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.75, height: 36, border: '1px solid #dde6f0', borderRadius: '10px', bgcolor: 'white', cursor: (txTotalCount === 0 || exporting) ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, color: (txTotalCount === 0 || exporting) ? '#cbd5e1' : '#0a326d' }}
          >
            {exporting ? <CircularProgress size={14} sx={{ color: '#cbd5e1' }} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            <LocalText>Export Report</LocalText>
          </Box>
        </Box>

        {/* Filter row */}
        <Box sx={{ px: 3, py: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography fontSize={11} fontWeight={600} sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}><LocalText>From</LocalText></Typography>
            <CustomDatePicker value={dateFrom} max={dateTo || undefined} onChange={(e) => handleDateFromChange(e.target.value)} placeholder="Start date" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography fontSize={11} fontWeight={600} sx={{ color: isDateRangeError ? '#ef4444' : '#94a3b8', whiteSpace: 'nowrap' }}><LocalText>To</LocalText></Typography>
            <CustomDatePicker value={dateTo} min={dateFrom || undefined} onChange={(e) => handleDateToChange(e.target.value)} placeholder="End date" isError={isDateRangeError} />
          </Box>
          <Select size="small" value={transactionType} onChange={(e) => setTransactionType(e.target.value)} displayEmpty sx={{ ...selectFieldSx, fontSize: 13, minWidth: 160 }} MenuProps={dropdownMenuProps}>
            <MenuItem value=""><LocalText>All Types</LocalText></MenuItem>
            <MenuItem value="Purchase"><LocalText>Purchase</LocalText></MenuItem>
            <MenuItem value="Refund"><LocalText>Refund</LocalText></MenuItem>
          </Select>
          {(dateFrom || dateTo || transactionType) && (
            <Box component="button" onClick={clearFilters} sx={{ px: 1.5, height: 32, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#64748b' }}>
              <LocalText>Clear</LocalText>
            </Box>
          )}
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          {loadingTx ? (
            <Box display="flex" justifyContent="center" p={6}><CircularProgress sx={{ color: '#0a326d' }} /></Box>
          ) : (
            <Table sx={{ minWidth: showGlobalFilters ? 1900 : 1500 }}>
              <TableHead>
                <TableRow>
                  {/* Entity/Space — global (gms_accountant) view only. Every row in this table
                      belongs to the same one deposit, which belongs to exactly one space, so
                      these are constant for the whole table (from the summary, not per-row
                      data) — shown here anyway for the same structural parity with Transaction
                      History the rest of this table already follows. */}
                  {showGlobalFilters && <TableCell sx={colHead}><LocalText>Entity</LocalText></TableCell>}
                  {showGlobalFilters && <TableCell sx={colHead}><LocalText>Entity ID</LocalText></TableCell>}
                  {showGlobalFilters && <TableCell sx={colHead}><LocalText>Space</LocalText></TableCell>}
                  {showGlobalFilters && <TableCell sx={colHead}><LocalText>Space ID</LocalText></TableCell>}
                  <TableCell sx={colHead}><LocalText>Payment Intent ID</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Transaction Type</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Status</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Customer Email / ID</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Customer Name</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Gateway</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Payment Method</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Date</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Currency</LocalText></TableCell>
                  <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Charged Amt</LocalText></TableCell>
                  <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Paid / Refund Amt</LocalText></TableCell>
                  <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Gateway Fee</LocalText></TableCell>
                  <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>FX Rate</LocalText></TableCell>
                  <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Contract %</LocalText></TableCell>
                  <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Net Amount</LocalText></TableCell>
                  <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Settled Amount</LocalText></TableCell>
                  <TableCell sx={colHead}><LocalText>Refund Reason</LocalText></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showGlobalFilters ? 20 : 16} sx={{ textAlign: 'center', py: 6, color: '#94a3b8', fontSize: 13 }}>
                      <LocalText>No transactions match these filters.</LocalText>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((tx) => (
                    <TableRow key={`${tx.chargeId}-${tx.status}-${tx.eventDate}`} sx={{ '&:hover': { bgcolor: '#fafbfc' }, '&:last-child td': { border: 0 } }}>
                      {showGlobalFilters && (
                        <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                          <Typography fontSize={12} fontWeight={600} sx={{ color: '#1e293b' }}>{summary?.entityName ?? '—'}</Typography>
                        </TableCell>
                      )}
                      {showGlobalFilters && (
                        <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                          <Typography fontSize={12} sx={{ color: '#64748b', fontFamily: 'monospace' }}>{summary?.entityId ?? '—'}</Typography>
                        </TableCell>
                      )}
                      {showGlobalFilters && (
                        <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                          <Typography fontSize={12} sx={{ color: '#475569' }}>{summary?.spaceName ?? '—'}</Typography>
                        </TableCell>
                      )}
                      {showGlobalFilters && (
                        <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                          <Typography fontSize={12} sx={{ color: '#64748b', fontFamily: 'monospace' }}>{summary?.spaceId ?? '—'}</Typography>
                        </TableCell>
                      )}
                      <TableCell sx={colCell}>
                        <Typography fontSize={11} fontWeight={700} sx={{ color: '#1e293b', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{tx.paymentIntentId}</Typography>
                      </TableCell>
                      <TableCell sx={colCell}><TxnTypeBadge type={tx.transactionType} /></TableCell>
                      <TableCell sx={colCell}><StatusBadge status={tx.status} /></TableCell>
                      <TableCell sx={colCell}>
                        <Typography fontSize={12} sx={{ color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{tx.customer ?? '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                        <Typography fontSize={12} sx={{ color: '#475569' }}>{tx.customerName ?? '—'}</Typography>
                      </TableCell>
                      <TableCell sx={colCell}>
                        {tx.gateway ? <GatewayBadge gateway={tx.gateway} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ ...colCell, color: '#64748b', whiteSpace: 'nowrap' }}>{tx.paymentMethod ?? '—'}</TableCell>
                      <TableCell sx={{ ...colCell, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(tx.eventDate)}</TableCell>
                      <TableCell sx={{ ...colCell, color: '#64748b' }}>{tx.currency ?? '—'}</TableCell>

                      {/* Charged Amt */}
                      <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Typography fontSize={13} sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                          {tx.chargedAmount != null ? formatAmount(tx.chargedAmount, tx.currency) : <span style={{ color: '#94a3b8' }}>—</span>}
                        </Typography>
                      </TableCell>

                      {/* Paid / Refund Amt */}
                      <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Typography fontSize={13} fontWeight={700} sx={{ color: isRefundedStatus(tx.status) ? '#7c3aed' : '#15803d', whiteSpace: 'nowrap' }}>
                          {isRefundedStatus(tx.status) ? '−' : ''}{formatAmount(tx.amount, tx.currency)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {tx.gatewayFee != null ? formatAmount(tx.gatewayFee, tx.currency) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {tx.exchangeRate != null ? Number(tx.exchangeRate).toFixed(4) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {tx.contractPercentageApplied != null ? `${Number(tx.contractPercentageApplied).toFixed(2)}%` : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {tx.netAmount != null ? formatAmount(tx.netAmount, tx.currency) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>

                      {/* Settled Amount — the actual amount credited to the merchant balance,
                          in ITS OWN currency (merchantNetCurrency), which can legitimately
                          differ from this row's Currency column — this is the ground-truth
                          PSP figure, never summed directly (see Net Amount/Total Amount above). */}
                      <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {tx.merchantNetAmount != null
                          ? formatAmount(tx.merchantNetAmount, tx.merchantNetCurrency ?? tx.currency)
                          : <span style={{ color: '#94a3b8' }}>—</span>}
                      </TableCell>

                      <TableCell sx={{ ...colCell, color: '#64748b', maxWidth: 180 }}>{tx.refundReason ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Box>

        {!loadingTx && (
          <Box sx={{ p: 3, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
              <LocalText>Showing</LocalText>{' '}
              <Box component="span" fontWeight={700} sx={{ color: '#1e293b' }}>
                {txTotalCount === 0 ? 0 : txPage * TX_PAGE_SIZE + 1}–{Math.min((txPage + 1) * TX_PAGE_SIZE, txTotalCount)}
              </Box>{' '}
              <LocalText>of</LocalText>{' '}
              <Box component="span" fontWeight={700} sx={{ color: '#1e293b' }}>{txTotalCount}</Box>{' '}
              <LocalText>transactions</LocalText>
            </Typography>

            <Box display="flex" alignItems="center" gap={0.75}>
              <Box component="button" onClick={() => setTxPage((p) => Math.max(0, p - 1))} disabled={txPage === 0}
                sx={{ px: 2, py: 0.75, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', fontSize: 12, fontWeight: 500, color: txPage === 0 ? '#cbd5e1' : '#64748b', cursor: txPage === 0 ? 'not-allowed' : 'pointer' }}>
                <LocalText>Previous</LocalText>
              </Box>
              <Typography fontSize={12} sx={{ color: '#64748b', px: 1 }}>
                <LocalText>Page</LocalText> {txPage + 1} <LocalText>of</LocalText> {txTotalPages}
              </Typography>
              <Box component="button" onClick={() => setTxPage((p) => Math.min(txTotalPages - 1, p + 1))} disabled={txPage >= txTotalPages - 1}
                sx={{ px: 2, py: 0.75, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', fontSize: 12, fontWeight: 500, color: txPage >= txTotalPages - 1 ? '#cbd5e1' : '#64748b', cursor: txPage >= txTotalPages - 1 ? 'not-allowed' : 'pointer' }}>
                <LocalText>Next</LocalText>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <WireDocumentUploadDialog
        open={uploadDialogOpen}
        mode={hasDocument ? 'replace' : 'upload'}
        uploading={uploading}
        onClose={() => setUploadDialogOpen(false)}
        onConfirm={handleUploadConfirm}
      />
    </Box>
  );
};

export default DepositDetail;
